import { PontCore } from '@/services/pontCore';
import { configureApiAuth } from '@verifime/api-definition';
import {
  initiateSso,
  SsoPortal,
  usePasswordless,
  useTokens,
} from '@verifime/cognito-passwordless-auth';
import { LoadingScreen } from '@verifime/components';
import { responseInterceptor } from '@verifime/utils';
import { UserProvider } from '@verifime/views';
import { useRouter } from 'next/router';
import React from 'react';
import { useCohortAssignment } from '../hooks/useCohortAssignment';

/**
 * AuthenticatedContainer ensures that only authenticated users can access the
 * components wrapped within it.
 *
 * For a detailed explanation of the different authentication patterns used
 * across the portals, see the central documentation here:
 * /docs/authentication-patterns.md
 */
export default function AuthenticatedContainer({ children }: { children: React.ReactElement }) {
  const { tokensParsed, signInStatus } = usePasswordless();
  const { getTokens, getTokensParsed } = useTokens();
  const router = useRouter();
  useCohortAssignment(tokensParsed?.idToken?.sub);

  React.useEffect(() => {
    const validateTokensParsed = (tokensParsedToValidate: typeof tokensParsed) =>
      tokensParsedToValidate && new Date() < tokensParsedToValidate.expireAt;

    if (!validateTokensParsed(tokensParsed)) {
      getTokensParsed().then((newTokensParsed) => {
        if (!validateTokensParsed(newTokensParsed)) {
          const currentPath = router.pathname;

          // Deep link SSO: if ?idp=xero is present, redirect directly to SSO
          const idp = router.query.idp;
          if (idp === 'xero') {
            // Preserve all query params except idp in the returnPath
            const url = new URL(router.asPath, window.location.origin);
            url.searchParams.delete('idp');
            const returnPath = url.pathname + (url.search || '');

            initiateSso({
              provider: 'xero',
              origin: window.location.origin,
              returnPath,
              portalEnv: (process.env.NEXT_PUBLIC_AUTH_PORTAL_ID as SsoPortal) || undefined,
            })
              .then((redirectUrl) => {
                window.location.href = redirectUrl;
              })
              .catch((err) => {
                console.error('Deep link SSO initiation failed:', err);
                if (currentPath !== '/') {
                  router.push({
                    pathname: '/',
                    query: { from: encodeURIComponent(router.asPath) },
                  });
                }
              });
            return;
          }

          if (currentPath !== '/') {
            router.push({
              pathname: '/',
              query: { from: encodeURIComponent(router.asPath) },
            });
          }
        }
      });
    }
  }, [getTokensParsed, router, tokensParsed]);

  PontCore.useFetch(async (url, options) => {
    const { accessToken: token } = (await getTokens()) || {};
    return await responseInterceptor(url, token, options);
  });

  // Configure token getter synchronously during render so it is in place
  // before any child component effects fire (child effects run before parent effects).
  configureApiAuth(async () => getTokens().then(({ accessToken }) => accessToken));

  // "/" is the public sign-in page. _app.tsx wraps it in AuthenticatedContainer
  // too (it is not in UNAUTHENTICATED_PATHS), and the redirect effect above
  // already special-cases it (no push when currentPath === '/'). Mirror that
  // here: only gate the render for paths other than "/", i.e. the actually
  // protected pages. Never render THOSE until the session is confirmed signed
  // in - the redirect above is asynchronous, so rendering children
  // unconditionally would flash gated content at unauthenticated visitors
  // before it fires.
  const requiresAuth = router.pathname !== '/';
  // A usable session means SIGNED_IN, or REFRESHING_SIGN_IN (an established
  // session silently refreshing an expired token). Keep the page mounted for
  // both, so a background refresh does not tear down in-progress page state.
  // Every other status (CHECKING, SIGNING_IN, NOT_SIGNED_IN, SIGNING_OUT) means
  // no usable session yet, so show the loader instead of flashing gated content.
  const hasSession = signInStatus === 'SIGNED_IN' || signInStatus === 'REFRESHING_SIGN_IN';
  if (requiresAuth && !hasSession) {
    return (
      <UserProvider tokens={tokensParsed}>
        <LoadingScreen data-testid="auth-gate-loading" />
      </UserProvider>
    );
  }

  return <UserProvider tokens={tokensParsed}>{children}</UserProvider>;
}
